import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Package, Plus, Trash2, X, FileSpreadsheet } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { BarangCombobox } from "./BarangCombobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import api, { formatApiError } from "../api";

const fmtRp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;
const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v + "T00:00:00");
  return isNaN(d) ? v : d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
};

const PemakaianDialog = ({ open, onClose, kendaraan, onSaved }) => {
  const [gudang, setGudang] = useState([]);
  const [plat, setPlat] = useState("");
  const [barangId, setBarangId] = useState("");
  const [jumlah, setJumlah] = useState("1");
  const [tanggal, setTanggal] = useState("");
  const [ket, setKet] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/gudang").then(({ data }) => setGudang(data)).catch(() => {});
      setPlat(""); setBarangId(""); setJumlah("1"); setTanggal(""); setKet("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!plat || !barangId) { toast.error("Pilih truk dan barang terlebih dahulu"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/armada/pemakaian", {
        Plat_Truk: plat, barang_id: barangId, Jumlah_Dipakai: parseInt(jumlah, 10),
        Tanggal: tanggal || null, Keterangan: ket || null,
      });
      toast.success(`${plat} memakai ${data.Nama_Barang} x${data.Jumlah_Dipakai} — stok tersisa ${data.Stok_Tersisa}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pemakaian-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Catat Pemakaian Sparepart</DialogTitle>
          <DialogDescription>Stok gudang otomatis berkurang dan tercatat di kartu stok.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Truk</Label>
            <Select value={plat} onValueChange={setPlat}>
              <SelectTrigger data-testid="pemakaian-truk-select"><SelectValue placeholder="Pilih truk" /></SelectTrigger>
              <SelectContent>
                {kendaraan.map((k) => (
                  <SelectItem key={k.id} value={k.Plat_Nomor}>{k.Plat_Nomor} — {k.Jenis_Truk}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Barang (dari Inventaris Gudang)</Label>
            <BarangCombobox gudang={gudang} value={barangId} onChange={setBarangId} testId="pemakaian-barang-select" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Jumlah Dipakai</Label>
              <Input data-testid="pemakaian-jumlah-input" type="number" min="1" step="1" value={jumlah}
                onChange={(e) => setJumlah(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label>Tanggal (opsional)</Label>
              <Input data-testid="pemakaian-tanggal-input" type="date" value={tanggal}
                onChange={(e) => setTanggal(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Keterangan (opsional)</Label>
            <Input data-testid="pemakaian-ket-input" value={ket} onChange={(e) => setKet(e.target.value)}
              placeholder="cth: ganti ban depan kanan" />
          </div>
          <Button data-testid="pemakaian-submit-button" type="submit" disabled={saving}
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" /> Simpan Pemakaian</>}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const PemakaianSparepart = ({ kendaraan, isAdmin }) => {
  const [items, setItems] = useState(null);
  const [rekap, setRekap] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = () => {
    api.get("/armada/pemakaian").then(({ data }) => setItems(data)).catch(() => setItems([]));
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/armada/pemakaian/rekap", { params }).then(({ data }) => setRekap(data)).catch(() => setRekap({ per_truk: [], total_biaya: 0 }));
  };
  useEffect(load, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportRekap = async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get("/armada/pemakaian/rekap/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Rekap_Sparepart.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Rekap sparepart diekspor ke Excel");
    } catch (err) {
      toast.error("Gagal mengekspor rekap");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Hapus pemakaian ${item.Nama_Barang} x${item.Jumlah_Dipakai} (${item.Plat_Truk})? Stok akan dikembalikan.`)) return;
    try {
      await api.delete(`/armada/pemakaian/${item.id}`);
      toast.success("Pemakaian dihapus, stok dikembalikan");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Card className="border-zinc-200 shadow-sm" data-testid="pemakaian-sparepart-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
            <Package className="w-4 h-4 text-emerald-700" /> Pemakaian Sparepart per Truk
          </CardTitle>
          {isAdmin && (
            <Button data-testid="add-pemakaian-button" size="sm" onClick={() => setDialogOpen(true)}
              className="bg-emerald-700 hover:bg-emerald-800 text-white">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Catat Pemakaian
            </Button>
          )}
        </div>
        <p className="text-xs text-zinc-500">Pemakaian langsung tanpa catatan servis — stok gudang terpotong otomatis. Rekap di bawah menggabungkan pemakaian langsung + via servis.</p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium mb-2">Riwayat Pemakaian Langsung</p>
          {items === null ? (
            <div className="flex justify-center py-6 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : items.length === 0 ? (
            <p data-testid="pemakaian-empty" className="py-4 text-center text-sm text-zinc-400">Belum ada pemakaian langsung. Klik "Catat Pemakaian".</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pemakaian-table">
                <thead>
                  <tr className="border-y border-zinc-200 bg-zinc-50">
                    {["Tanggal", "Plat Truk", "Barang", "Jumlah", "Subtotal", "Keterangan", isAdmin ? "Aksi" : null].filter(Boolean).map((h) => (
                      <th key={h} className="text-left px-3 py-2 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.slice(0, 20).map((it) => (
                    <tr key={it.id} data-testid={`pemakaian-row-${it.id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(it.Tanggal)}</td>
                      <td className="px-3 py-2 font-medium text-zinc-900 whitespace-nowrap">{it.Plat_Truk}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{it.Kode_Barang} — {it.Nama_Barang}</td>
                      <td className="px-3 py-2 tabular-nums text-center">{it.Jumlah_Dipakai}</td>
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap">{fmtRp(it.Subtotal)}</td>
                      <td className="px-3 py-2 text-zinc-500 max-w-[180px] truncate">{it.Keterangan || "-"}</td>
                      {isAdmin && (
                        <td className="px-3 py-2">
                          <Button data-testid={`delete-pemakaian-${it.id}`} size="icon" variant="ghost"
                            className="h-7 w-7 text-zinc-400 hover:text-red-600" onClick={() => remove(it)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Rekap Pemakaian per Truk (langsung + via servis)</p>
            <div className="flex items-center gap-2">
              <Input data-testid="rekap-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-36 bg-white" />
              <span className="text-xs text-zinc-400">s/d</span>
              <Input data-testid="rekap-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-36 bg-white" />
              {(dateFrom || dateTo) && (
                <Button data-testid="rekap-reset-dates" size="icon" variant="outline" className="h-8 w-8 border-zinc-300"
                  onClick={() => { setDateFrom(""); setDateTo(""); }}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              )}
              <Button data-testid="rekap-export-button" size="sm" variant="outline" onClick={exportRekap}
                className="h-8 border-emerald-300 text-emerald-800 hover:bg-emerald-50">
                <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Excel
              </Button>
            </div>
          </div>
          {rekap === null ? (
            <div className="flex justify-center py-6 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : rekap.per_truk.length === 0 ? (
            <p data-testid="rekap-empty" className="py-4 text-center text-sm text-zinc-400">Belum ada pemakaian sparepart pada periode ini.</p>
          ) : (
            <div className="space-y-3">
              {rekap.per_truk.map((p) => (
                <div key={p.Plat_Truk} data-testid={`rekap-truk-${p.Plat_Truk.replace(/ /g, "-")}`}
                  className="border border-zinc-200 rounded-md overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 bg-zinc-50 border-b border-zinc-200">
                    <span className="text-sm font-semibold text-zinc-900">{p.Plat_Truk}</span>
                    <span className="text-sm font-semibold text-emerald-800 tabular-nums">
                      {p.total_jumlah} item — {fmtRp(p.total_biaya)}
                    </span>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {p.items.map((it) => (
                        <tr key={it.Kode_Barang} className="border-b border-zinc-100 last:border-0">
                          <td className="px-3 py-1.5">{it.Kode_Barang} — {it.Nama_Barang}</td>
                          <td className="px-3 py-1.5 text-xs text-zinc-500 whitespace-nowrap">
                            {it.langsung > 0 && `langsung x${it.langsung}`}{it.langsung > 0 && it.via_servis > 0 && ", "}
                            {it.via_servis > 0 && `via servis x${it.via_servis}`}
                          </td>
                          <td className="px-3 py-1.5 tabular-nums text-center w-16">x{it.jumlah}</td>
                          <td className="px-3 py-1.5 tabular-nums text-right whitespace-nowrap w-32">{fmtRp(it.biaya)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              <p data-testid="rekap-total-biaya" className="text-right text-sm font-semibold text-emerald-800">
                Total biaya sparepart semua truk: {fmtRp(rekap.total_biaya)}
              </p>
            </div>
          )}
        </div>
      </CardContent>
      <PemakaianDialog open={dialogOpen} onClose={() => setDialogOpen(false)} kendaraan={kendaraan} onSaved={load} />
    </Card>
  );
};
